import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setStoredToken } from "../lib/api";
import { trackLeadConversion } from "../lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the JWT so subsequent requests work even when Safari ITP
      // silently drops the httpOnly cookie. The bearer token is sent as
      // an Authorization header by the axios interceptor.
      if (data?.access_token) setStoredToken(data.access_token);
      await fetchMe();
      return data;
    },
    [fetchMe]
  );

  const register = useCallback(
    async (email, password, country, firstName = "", lastName = "") => {
      const { data } = await api.post("/auth/register", {
        email,
        password,
        country,
        first_name: firstName,
        last_name: lastName,
      });
      if (data?.access_token) setStoredToken(data.access_token);
      await fetchMe();
      // Google Ads lead conversion — fires only if user consented to marketing.
      // Enhanced Conversions: pass email so gtag can hash & attribute the lead.
      trackLeadConversion("signup", { email, firstName, lastName });
      return data;
    },
    [fetchMe]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network errors are non-fatal — we still clear local state below.
      if (process.env.NODE_ENV === "development") {
        console.debug("[auth] logout request failed:", err);
      }
    }
    setStoredToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        isMember: user?.role === "member" || user?.role === "admin",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
